import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { acceptInvite } from '@/_shared/lib/accept-invite';
import { orpc } from '@/_shared/lib/orpc';
import type { client } from '@/_shared/lib/rpc';

export type PendingInvite = Awaited<ReturnType<typeof client.workspace.listMyInvites>>['invites'][number];

/** How often an open tab reads the invites again. A hidden tab does not, and reads them when it gets the focus. */
const PENDING_INVITES_REFETCH_MS = 60_000;

/**
 * The signed-in person's live invites, in workspaces that they are not in yet (ACT2-1124). The pickers and the
 * workspace switcher show them, so a person who already uses Lookout sees an invite without the email link. No push
 * tells the page about a new invite, so the list is read again every minute while the tab is visible.
 */
export function usePendingInvites() {
  return useQuery({
    ...orpc.workspace.listMyInvites.queryOptions(),
    refetchInterval: PENDING_INVITES_REFETCH_MS,
  });
}

/** "Admin" or "Member", for the invite buttons. */
export function inviteRoleLabel(role: PendingInvite['role']): string {
  return role === 'admin' ? 'Admin' : 'Member';
}

/**
 * Accepts an invite and opens its workspace. The server gives the invite's role, and in a sibling workspace a Lookout
 * admin also gets the grant sets that wait for the join.
 */
export function useAcceptInvite() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (inviteId: string) => acceptInvite(inviteId),
    onSuccess: async (workspace) => {
      // Mark every query old, but reload none now: the client reads the workspace from the URL, so a reload during the
      // navigation could store the new workspace's answers under the old workspace's keys. The new pages read on mount.
      void queryClient.invalidateQueries({ refetchType: 'none' });
      // The workspace route checks the membership in auth.me, so load the new membership before the navigation.
      await queryClient.refetchQueries({ queryKey: orpc.auth.me.key() });
      void navigate(`/w/${workspace.id}`);
    },
    onError: async (err: Error) => {
      // A closed invite leaves the list: read it again so that the card goes away.
      await queryClient.invalidateQueries({ queryKey: orpc.workspace.listMyInvites.key() });
      toast.error(err.message || 'Could not accept the invite. Try again.');
    },
  });
}
